import { useState } from 'react';

import styles from '../ScenarioGenerator.module.css';
import { MotionModelSelect } from '../../common/MotionModelSelect';
import { MotionQualitySelect } from '../../common/MotionQualitySelect';
import {
  MOTION_LAYERED_ASPECT_OPTIONS,
  MOTION_LAYERED_DURATION_MAX,
  MOTION_LAYERED_DURATION_MIN,
  clampMotionLayeredDuration,
  type MotionLayeredEndState,
  type MotionLayeredSettings,
  type MotionLayeredSoundEffects,
} from '../../../../features/motion/layeredPromptMode';

/**
 * プロンプト入力モードの 3 タブ目「モーショングラフィックス」の設定ブロック
 * (docs/features/motion-graphics-mode-plan.md §5.3)。
 * 動画設定 (モデル・解像度・秒課金の見積もり・参照動画) の代わりに出す。
 * 値は呼び出し側 (layeredSettingsStore) が持ち、生成の body に載る (useVideoHandlers)。
 * 料金は availability の定額 (credit_rate_config)。読めない / 使えないときは理由を出し、
 * 生成ボタンは呼び出し側で無効にする (fail-closed)。
 */
export function MotionGraphicsSettingsSection({
  settings,
  onSettingsChange,
  assetNotice,
  disabled = false,
  layerMaterial = null,
  pendingLayerMaterialName = null,
  onOpenLayers,
  credits = null,
  unavailableMessage = null,
  aspectNotice = null,
}: {
  settings: MotionLayeredSettings;
  onSettingsChange: (next: MotionLayeredSettings) => void;
  /** 選択済み素材の注意 (getMotionLayeredAssetNotice)。null なら出さない */
  assetNotice: string | null;
  disabled?: boolean;
  /** 選択済みのレイヤー素材の要約。null ならレイヤー素材の行を出さない (注意文だけ) */
  layerMaterial?: { name: string; layerCount: number; lockedCount: number } | null;
  /** SVG 原本でレイヤー構成が未解析 (生成ボタン押下時に解析する) ときの素材名 */
  pendingLayerMaterialName?: string | null;
  /** 「レイヤーを確認・編集」(レイヤーのモーダルを開く) */
  onOpenLayers?: () => void;
  /** 1 件あたりの消費ポイント (availability.credits)。使えないときは null */
  credits?: number | null;
  /** 使えない理由 (motionLayeredUnavailableMessage)。使えるときは null */
  unavailableMessage?: string | null;
  /** 選んだ比率がカンバスと違うときの注意 (motionLayeredCanvasAspectNotice)。null なら出さない */
  aspectNotice?: string | null;
}) {
  const [isOpen, setIsOpen] = useState(true);
  // 入力途中 (空欄・範囲外) を許すため、表示用の文字列は別に持ち、確定 (blur / Enter) で 2〜16 秒・小数 1 桁に収める
  const [durationText, setDurationText] = useState(String(settings.durationSeconds));

  const commitDuration = () => {
    // 空欄・数でない入力は直前の値に戻す (空欄を 0 と読んで 2 秒にしない)
    const next = clampMotionLayeredDuration(durationText) ?? settings.durationSeconds;
    setDurationText(String(next));
    if (next !== settings.durationSeconds) {
      onSettingsChange({ ...settings, durationSeconds: next });
    }
  };

  return (
    <>
      {layerMaterial && (
        <div className={styles.motionLayerMaterialRow} data-testid="motion-layer-material-row">
          <span className={styles.motionLayerMaterialName} title={layerMaterial.name}>{layerMaterial.name}</span>
          <span className={styles.motionLayerMaterialSummary} data-testid="motion-layer-material-summary">
            {`${layerMaterial.layerCount} レイヤー・固定 ${layerMaterial.lockedCount}`}
          </span>
          {onOpenLayers && (
            <button type="button" className="btn-modal-secondary" onClick={onOpenLayers}>
              レイヤーを確認・編集
            </button>
          )}
        </div>
      )}
      {!layerMaterial && pendingLayerMaterialName && (
        <div className={styles.motionLayerMaterialRow} data-testid="motion-layer-material-pending">
          <span className={styles.motionLayerMaterialName} title={pendingLayerMaterialName}>{pendingLayerMaterialName}</span>
          <span className={styles.motionLayerMaterialSummary}>生成時に SVG のレイヤーを解析します</span>
        </div>
      )}
      {assetNotice && (
        <div className={styles.motionLayeredAssetNotice} role="status" data-testid="motion-layered-asset-notice">
          {assetNotice}
        </div>
      )}
      <div
        className={`accordion-section ${isOpen ? 'accordion-section-open' : ''}`}
        data-testid="motion-layered-settings"
      >
        <button
          type="button"
          className="accordion-header"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
        >
          <span className="accordion-title">
            <img src="/scene_motion.svg" alt="" className="accordion-title-icon" />
            <span>モーショングラフィックスの設定</span>
            <span className="accordion-summary">
              {settings.durationSeconds}秒 / {settings.aspectRatio}
            </span>
          </span>
          <span className={`accordion-icon ${isOpen ? 'accordion-icon-open' : ''}`}>
            <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" style={{ transition: 'transform 0.2s', transform: isOpen ? 'rotate(90deg)' : 'none' }}><path d="M2 0l4 4-4 4z" /></svg>
          </span>
        </button>
        <div className={`accordion-panel ${isOpen ? 'accordion-panel-open' : 'accordion-panel-closed'}`}>
          <div className="form-grid">
            <label className="gen-label gen-label-narrow">
              <span className="gen-label-text">尺 (秒)</span>
              <input
                type="number"
                min={MOTION_LAYERED_DURATION_MIN}
                max={MOTION_LAYERED_DURATION_MAX}
                step={0.1}
                value={durationText}
                onChange={(e) => setDurationText(e.target.value)}
                onBlur={commitDuration}
                onKeyDown={(e) => {
                  // フォームの送信 (= 通常の動画生成) に流さず、入力の確定だけにする
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitDuration();
                  }
                }}
                disabled={disabled}
                className="has-value"
                data-testid="motion-layered-duration"
              />
            </label>
            <label className="gen-label gen-label-narrow">
              <span className="gen-label-text">比率</span>
              <select
                value={settings.aspectRatio}
                onChange={(e) => onSettingsChange({ ...settings, aspectRatio: e.target.value })}
                disabled={disabled}
                className="has-value"
                data-testid="motion-layered-aspect"
              >
                {MOTION_LAYERED_ASPECT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
          </div>
          {aspectNotice && (
            <div className={styles.generateGuidance} role="status" data-testid="motion-layered-aspect-notice">
              {aspectNotice}
            </div>
          )}
          <div className="form-grid">
            <label className="gen-label gen-label-narrow">
              <span className="gen-label-text">効果音</span>
              <select
                value={settings.soundEffects}
                onChange={(e) => onSettingsChange({ ...settings, soundEffects: e.target.value as MotionLayeredSoundEffects })}
                disabled={disabled}
                className="has-value"
                data-testid="motion-layered-sound-effects"
              >
                <option value="none">なし</option>
                <option value="auto">自動</option>
              </select>
            </label>
            <label className="gen-label gen-label-narrow">
              <span className="gen-label-text">終了時の状態</span>
              <select
                value={settings.endState}
                onChange={(e) => onSettingsChange({ ...settings, endState: e.target.value as MotionLayeredEndState })}
                disabled={disabled}
                className="has-value"
                data-testid="motion-layered-end-state"
              >
                <option value="composite">入稿デザインと同じ</option>
                <option value="free">自由</option>
              </select>
            </label>
          </div>
          <div style={{ marginTop: 8 }}>
            <MotionQualitySelect route="layered" disabled={disabled} testId="motion-layered-quality" />
          </div>
          <div style={{ marginTop: 8 }}>
            <MotionModelSelect disabled={disabled} testId="motion-layered-model" />
          </div>
          <div className={styles.generateGuidance} data-testid="motion-layered-price">
            {unavailableMessage
              ? unavailableMessage
              : credits !== null
                ? `料金: ${credits} ポイント / 件 (尺・仕上げ方に関わらず定額)`
                : '料金を確認しています…'}
          </div>
        </div>
      </div>
    </>
  );
}
